import { useEffect } from 'react';
import { Loader2, TrendingUp } from 'lucide-react';
import { useFleetStore } from '@/stores/fleet';
import type { NviSnapshotRecord } from '@/services/api';

function formatComponent(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function TrendSparkline({ snapshots }: { snapshots: NviSnapshotRecord[] }) {
  if (snapshots.length === 0) return null;
  // Reverse to chronological order for the chart.
  const ordered = [...snapshots].sort((a, b) =>
    (a.timestamp ?? '').localeCompare(b.timestamp ?? ''),
  );
  const values = ordered.map((s) => s.nvi_score);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(max - min, 0.01);
  const width = 260;
  const height = 48;
  const step = ordered.length > 1 ? (width - 4) / (ordered.length - 1) : 0;

  const points = ordered
    .map((s, i) => {
      const x = 2 + i * step;
      const y = 2 + (1 - (s.nvi_score - min) / range) * (height - 4);
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div className="flex items-end justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
          NVI Trend ({ordered.length} snapshots)
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          min {min.toFixed(1)} · max {max.toFixed(1)}
        </div>
      </div>
      <svg width={width} height={height}>
        <polyline
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          className="text-brand-teal"
          points={points}
        />
      </svg>
    </div>
  );
}

export function NVIProjectionsPanel() {
  const {
    nvi,
    nviProjections,
    nviSnapshots,
    projectionsLoading,
    fetchNviProjections,
    fetchNviSnapshots,
  } = useFleetStore();

  useEffect(() => {
    fetchNviProjections();
    fetchNviSnapshots();
  }, [fetchNviProjections, fetchNviSnapshots]);

  const current = nviProjections?.current_nvi ?? nvi?.nvi_score ?? 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp size={16} className="text-brand-purple" />
          <h2 className="font-semibold text-navy-900 text-sm">NVI Projections</h2>
        </div>
        {projectionsLoading && (
          <Loader2 size={12} className="text-slate-400 animate-spin" />
        )}
      </div>

      {nviSnapshots.length > 0 && (
        <div className="border-b border-slate-100 pb-3">
          <TrendSparkline snapshots={nviSnapshots} />
        </div>
      )}

      <div>
        <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">
          Current NVI
        </div>
        <div className="text-2xl font-bold text-navy-900">{current.toFixed(2)}</div>
      </div>

      {nviProjections?.projections && nviProjections.projections.length > 0 ? (
        <div>
          <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-2">
            Scenarios
          </div>
          <div className="space-y-1.5">
            {nviProjections.projections.map((p, i) => (
              <div
                key={`${p.scenario}-${i}`}
                className="flex items-center justify-between text-xs border border-slate-200 rounded-lg px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-700 truncate">
                    {p.scenario}
                  </div>
                  {p.assumptions.length > 0 && (
                    <div className="text-[10px] text-slate-400 mt-0.5 truncate">
                      {p.assumptions.join(' · ')}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 text-right flex-shrink-0">
                  <span className="text-slate-500">
                    {p.current_nvi.toFixed(1)} → {p.projected_nvi.toFixed(1)}
                  </span>
                  <span
                    className={
                      p.delta >= 0 ? 'text-emerald-600 font-medium' : 'text-rose-600 font-medium'
                    }
                  >
                    {p.delta >= 0 ? '+' : ''}
                    {p.delta.toFixed(2)} ({p.delta_pct >= 0 ? '+' : ''}
                    {p.delta_pct.toFixed(1)}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-sm text-slate-400">No projection scenarios available.</div>
      )}

      {nviProjections?.components && (
        <div>
          <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-2">
            Components (current)
          </div>
          <div className="grid grid-cols-5 gap-2">
            {Object.entries(nviProjections.components).map(([k, v]) => (
              <div
                key={k}
                className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-center"
              >
                <div className="text-[9px] text-slate-400 uppercase">{formatComponent(k)}</div>
                <div className="text-sm font-semibold text-navy-900">
                  {typeof v === 'number' ? v.toFixed(1) : '—'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
